import type { CuratedAlbumKey } from './types/curated-album-key'
import type { CuratedAlbumIcon } from './types/curated-album-icon'
import type { AlbumSelection } from './types/album-selection'

export const CURATED_ALBUM_OPTIONS: ReadonlyArray<{
  key: CuratedAlbumKey
  label: string
  icon: CuratedAlbumIcon
}> = [
  { key: 'all-photos', label: 'All photos', icon: 'folders' },
  { key: 'favorites', label: 'My favorites', icon: 'star' },
  { key: 'recommended', label: 'Recommended for spread', icon: 'book-open' },
  { key: 'recommend-to-me', label: 'Recommended to me', icon: 'bookmark' },
  { key: 'snap', label: 'Snap approved', icon: 'check-check' },
]

const CURATED_PREFIX = 'curated:'
const ALBUM_PREFIX = 'album:'

export function encodeAlbumSelection(selection: AlbumSelection): string {
  if (selection.type === 'curated') return `${CURATED_PREFIX}${selection.key}`
  return `${ALBUM_PREFIX}${selection.albumId}`
}

export function decodeAlbumSelection(
  value: string | null,
  jobAlbums: ReadonlyArray<{ albumId: string; displayName: string }> = []
): AlbumSelection | null {
  if (!value) return null

  if (value.startsWith(CURATED_PREFIX)) {
    const key = value.slice(CURATED_PREFIX.length) as CuratedAlbumKey

    if (CURATED_ALBUM_OPTIONS.some((option) => option.key === key)) {
      return { type: 'curated', key }
    }

    return null
  }

  if (value.startsWith(ALBUM_PREFIX)) {
    const albumId = value.slice(ALBUM_PREFIX.length)

    if (!albumId) return null

    const album = jobAlbums.find((item) => item.albumId === albumId)

    return { type: 'album', albumId, displayName: album?.displayName ?? '' }
  }

  return null
}

export function curatedAlbumRequiresSpreadId(key: CuratedAlbumKey): boolean {
  return key === 'recommended'
}

export function canFetchCuratedAlbum(key: CuratedAlbumKey, spreadId?: string | null): boolean {
  if (curatedAlbumRequiresSpreadId(key)) return Boolean(spreadId)
  return true
}

export function getAlbumSelectionLabel(selection: AlbumSelection | null): string | null {
  if (!selection) return null

  if (selection.type === 'curated') {
    return CURATED_ALBUM_OPTIONS.find((option) => option.key === selection.key)?.label ?? null
  }

  return selection.displayName
}

export function isAllPhotosAlbumSelection(selection: AlbumSelection | null): boolean {
  return selection?.type === 'curated' && selection.key === 'all-photos'
}

/** Filename search is album-scoped unless All photos is selected (WMPCM-8599). */
export function shouldScopeFilenameSearchToAlbum(selection: AlbumSelection | null): boolean {
  return Boolean(selection) && !isAllPhotosAlbumSelection(selection)
}
